// Redacts known-secret values + generic token patterns from a text stream,
// tolerating a secret split across two chunks.
//
// Strategy: keep a RAW (never pre-redacted) tail buffer of unflushed text
// across `feed()` calls. Each call scans (held tail + new chunk) for every
// match interval (secret-match-intervals.ts: all occurrences, overlaps merged,
// widened to whole JSON escapes), emits only the part that no match could
// still extend past, and keeps the rest RAW. `flush()` redacts + emits the
// remaining tail (end of stream, nothing more can arrive to complete a match).
//
// Why raw (review 04a M2): the tail used to be stored already redacted — a
// short known value that is a PREFIX of a longer one (`BASE_URL` ⊂
// `WEBHOOK_URL?t=TOKEN`), cut right after the short one, got replaced early
// and the long value could no longer match: its tail (the token) streamed out.
//
// Hold-back length (re-review 2026-09-26, C1-F1): a FIXED 128 chars leaked
// the first (len - 128) chars of any longer secret — a real JWT is ~270+,
// measured intact at the sink. Bounds, the largest wins:
//   - a base of BASE_HOLD_BACK_CHARS, raised to 3× the longest known secret
//     value and to the longest encoded variant (JSON-escape twice can exceed 3×);
//   - the maximal token-like run (`[A-Za-z0-9._-]`, plus a "Bearer " prefix)
//     touching the END of the buffer — every generic pattern is made of those
//     chars, so a split token always ends the buffer with such a run. Bounded
//     so one giant base64 blob cannot pin the whole stream in memory;
//   - a match interval crossing the emit boundary is held whole (at most
//     MAX_HELD_INTERVAL_CHARS — past that, `cut` stays `holdBack` before the
//     interval's known end and the emitted head is redacted; the held rest is
//     redacted again later — double redaction accepted, review L3);
//   - +JSON_ESCAPE_MARGIN so a match starting inside a `\uXXXX` still finds its
//     escape in the buffer (review L2), and every `cut` is moved OUT of a JSON
//     escape before the crossing check (review M1 — a cut inside `\\` made
//     the next buffer miscount escape parity and emit `\[REDACTED]`).
//
// JWT-like detection is a linear scan (jwt-like-token-linear-scan.ts — the
// regex was ~O(n²) on `eyJ`-dense runs a worker can emit, review L5).

import { jwtLikeMatchIntervals } from "./jwt-like-token-linear-scan.js";
import { encodedVariantsOfKnownSecrets } from "./known-secret-value-encoded-variants.js";
import {
  alignCutOutsideJsonEscape,
  applyRedactionIntervals,
  literalMatchIntervals,
  type MatchInterval,
  mergeRedactionIntervals,
  patternMatchIntervals,
} from "./secret-match-intervals.js";

const BASE_HOLD_BACK_CHARS = 128;
const KNOWN_SECRET_HOLD_BACK_FACTOR = 3;
const MAX_TOKEN_RUN_HOLD_BACK_CHARS = 65_536;
const MAX_HELD_INTERVAL_CHARS = 65_536;
const JSON_ESCAPE_MARGIN = 6;
// Review L4: `Bearer` + tới 64 khoảng trắng trước run vẫn được giữ cùng token.
const BEARER_PREFIX_BEFORE_RUN = /Bearer\s{1,64}$/;
const BEARER_PREFIX_LOOKBACK = 72;

function isTokenChar(code: number): boolean {
  return (
    (code >= 48 && code <= 57) || // 0-9
    (code >= 65 && code <= 90) || // A-Z
    (code >= 97 && code <= 122) || // a-z
    code === 46 || // .
    code === 95 || // _
    code === 45 // -
  );
}

/** Length of the token-like run ending the text (+ its "Bearer " prefix), 0 when the text ends on a separator. */
function trailingTokenRunLength(text: string): number {
  let start = text.length;
  while (start > 0 && text.length - start < MAX_TOKEN_RUN_HOLD_BACK_CHARS) {
    if (!isTokenChar(text.charCodeAt(start - 1))) break;
    start--;
  }
  const run = text.length - start;
  if (run === 0) return 0;
  const prefix = BEARER_PREFIX_BEFORE_RUN.exec(
    text.slice(Math.max(0, start - BEARER_PREFIX_LOOKBACK), start),
  );
  return run + (prefix ? prefix[0].length : 0);
}

const GENERIC_TOKEN_PATTERNS: RegExp[] = [
  /sk-ant-[A-Za-z0-9_-]{10,}/g,
  /ghp_[A-Za-z0-9]{10,}/g,
  /glpat-[A-Za-z0-9_-]{10,}/g,
  /AKIA[A-Z0-9]{12,}/g,
  /Bearer\s+[A-Za-z0-9._-]{8,}/g,
];

/**
 * Phase 04a (B11, N16): che CHỈ theo known-values, thuần, một lượt theo khoảng
 * (hợp mọi lần xuất hiện của mọi biến thể — review 04a L4) — không chạy
 * `GENERIC_TOKEN_PATTERNS` (khác `SecretRedactor.feed()`). `values` rỗng ⇒
 * trả nguyên `text`.
 */
export function redactKnownValues(text: string, values: readonly string[]): string {
  if (values.length === 0 || text.length === 0) return text;
  return makeKnownValuesRedactor(values)(text);
}

/** Như `redactKnownValues`, tính biến thể MỘT lần cho nhiều chuỗi (sink che sâu — review 04a L10). */
export function makeKnownValuesRedactor(values: readonly string[]): (text: string) => string {
  const literals = encodedVariantsOfKnownSecrets(values);
  return (text) =>
    literals.length === 0 || text.length === 0
      ? text
      : applyRedactionIntervals(
          text,
          mergeRedactionIntervals(text, literalMatchIntervals(text, literals)),
        );
}

export interface SecretRedactorOptions {
  secretValues: readonly string[];
}

export class SecretRedactor {
  private readonly literals: string[];
  private readonly baseHoldBack: number;
  private tail = "";

  constructor(options: SecretRedactorOptions) {
    // Phase 04a (Codex F2): raw + base64 + URL-encode + JSON-escape 1 và 2 tầng.
    this.literals = encodedVariantsOfKnownSecrets(options.secretValues);
    const longestSecret = Math.max(0, ...options.secretValues.map((s) => s.length));
    this.baseHoldBack =
      Math.max(
        BASE_HOLD_BACK_CHARS,
        longestSecret * KNOWN_SECRET_HOLD_BACK_FACTOR,
        this.literals[0]?.length ?? 0,
      ) + JSON_ESCAPE_MARGIN;
  }

  private intervals(text: string, finalize: boolean): MatchInterval[] {
    return mergeRedactionIntervals(text, [
      ...literalMatchIntervals(text, this.literals),
      ...patternMatchIntervals(text, GENERIC_TOKEN_PATTERNS, finalize),
      ...jwtLikeMatchIntervals(text, finalize),
    ]);
  }

  // Emits safe-to-flush redacted text; holds back a RAW tail that a match
  // might still extend into from the NEXT chunk.
  feed(chunk: string): string {
    const buffer = this.tail + chunk;
    const holdBack = Math.max(this.baseHoldBack, trailingTokenRunLength(buffer));
    let cut = buffer.length - holdBack;
    const intervals = cut > 0 ? this.intervals(buffer, false) : [];
    // `cut` only moves back, so this settles: align out of a JSON escape, then
    // back off to the start of a match that crosses it (L3 cap for huge ones).
    for (let previous = -1; cut > 0 && cut !== previous; ) {
      previous = cut;
      cut = alignCutOutsideJsonEscape(buffer, cut);
      const at = cut;
      const crossing = intervals.find(([s, e]) => s < at && e > at);
      if (crossing === undefined) continue;
      cut =
        buffer.length - crossing[0] > MAX_HELD_INTERVAL_CHARS
          ? Math.max(crossing[0], crossing[1] - holdBack)
          : crossing[0];
    }
    if (cut <= 0) {
      this.tail = buffer;
      return "";
    }
    this.tail = buffer.slice(cut);
    // Clipped at `cut`: only the L3 branch can leave a match crossing it — its
    // emitted head is redacted, the held rest is redacted again later.
    const emitted = intervals
      .filter(([s]) => s < cut)
      .map(([s, e]): MatchInterval => [s, Math.min(e, cut)]);
    return applyRedactionIntervals(buffer.slice(0, cut), emitted);
  }

  // End of stream: redact + emit whatever tail remains.
  flush(): string {
    const out = applyRedactionIntervals(this.tail, this.intervals(this.tail, true));
    this.tail = "";
    return out;
  }
}
